import React, { useState, useEffect, useRef } from "react";
import { Button } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import HttpApis from "../../services/HttpApis";
import { isPlausibleEmail as validateEmail } from "../../utils/validateEmail";
import { Notyf } from "notyf";
import "notyf/notyf.min.css";
import * as Styled from "../Login/style";
import { MdKeyboardArrowLeft } from "react-icons/md";
import { useTenant } from "../../TenantContext";

const ForgotPassword = () => {
  const { logo } = useTenant();
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const timerRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const notyf = new Notyf({ position: { x: "center", y: "center" } });


  const calculateRemainingTime = () => {
    const expiryTime = localStorage.getItem("otpExpiryTime");
    if (!expiryTime) return 0;

    const remaining = Math.round((parseInt(expiryTime, 10) - Date.now()) / 1000);
    return remaining > 0 ? remaining : 0;
  };

  const startTimer = (secondsDuration = 60) => {
    if (timerRef.current) clearInterval(timerRef.current);
    let remaining = calculateRemainingTime();
    if (remaining <= 0) {
      const expiryTimestamp = Date.now() + secondsDuration * 1000;
      localStorage.setItem("otpExpiryTime", expiryTimestamp.toString());
      remaining = secondsDuration;
    }

    setTimer(remaining);
    setCanResend(false);

    timerRef.current = setInterval(() => {
      const timeLeft = calculateRemainingTime();
      if (timeLeft <= 0) {
        clearInterval(timerRef.current);
        setTimer(0);
        setCanResend(true);
        localStorage.removeItem("otpExpiryTime");
      } else {
        setTimer(timeLeft);
      }
    }, 1000);
  };

  useEffect(() => {
    const storedEmail = location.state?.email || localStorage.getItem("resetPasswordEmail");
    if (storedEmail) {
      setEmail(storedEmail);
      localStorage.setItem("resetPasswordEmail", storedEmail);
    }
    startTimer();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [location]);

  useEffect(() => {
    const handlePopState = (e) => {
      e.preventDefault();
      window.history.pushState(null, '', window.location.pathname);
    };

    window.history.pushState(null, '', window.location.pathname);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleVerifyOTP();
    }
  };

  const handleResendOTP = async () => {
    if (resending) return;
    setError("");
    if (!email) {
      setError("Email cannot be empty");
      return;
    }
    if (!validateEmail(email)) {
      setError("Invalid email format");
      return;
    }
    try {
      setLoading(true);
      setResending(true);
      const formData = new FormData();
      formData.append("email", email);

      const response = await HttpApis.forgotPassowrd(formData);
      if (response.status === 200) {
        localStorage.setItem("resetPasswordEmail", email);
        notyf.success("An OTP has been sent to your registered email address");
        localStorage.removeItem("otpExpiryTime");
        startTimer(60);

        navigate("/forgotPassword", { state: { email: email }, replace: true });
      } else {
        setError("Failed to send password reset email");
      }
    } catch (err) {
      if (err.errors) {
        const errorMessages = Object.values(err.errors).flat();
        setError(errorMessages.join(', '));
      } else if (err.message) {
        setError(err.message);
      } else if (err.response?.data?.errors) {
        const errorMessages = Object.values(err.response.data.errors).flat();
        setError(errorMessages.join(', '));
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError("An error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
      setResending(false);
    }
  };

  const handleVerifyOTP = async () => {
    setError("");
    setMessage("");

    if (!email) {
      setError("Email cannot be empty");
      return;
    }

    if (!validateEmail(email)) {
      setError("Invalid email format");
      return;
    }

    if (!otp) {
      setError("OTP cannot be empty");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("email", email);
      formData.append("otp", otp);

      const response = await HttpApis.verifyOtp(formData);

      if (response.status === 200) {
        setMessage("OTP verified successfully!");
        notyf.success("OTP verified successfully!");
        setError("");
        setMessage("");
        localStorage.removeItem("otpExpiryTime");
        localStorage.setItem("resetPasswordOtp", otp);
        localStorage.removeItem("resetPasswordEmail");
        navigate("/newPassword", { state: { email: email, otp: otp }, replace: true });
      } else {
        setError("Failed to verify OTP");
      }
    } catch (err) {
      if (err.errors) {
        const errorMessages = Object.values(err.errors).flat();
        setError(errorMessages.join(', '));
      } else if (err.message) {
        setError(err.message);
      } else if (err.response?.data?.errors) {
        const errorMessages = Object.values(err.response.data.errors).flat();
        setError(errorMessages.join(', '));
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError("An error occurred. Please try again.");
      }
    }
  };

  return (
    <Styled.LoginContainer>
      <Styled.MainContainer className="container">
        <div className="logo-container">
          <img src={logo} alt="Logo" className="logo" />
        </div>
        <div className="formContainer">
          <p className="para">Reset your password</p>
          <input
            placeholder="Enter your email"
            className="inputBox"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={true}
            style={{ opacity: 0.6, cursor: "not-allowed" }}
          />
          <input
            placeholder="Enter OTP"
            className="inputBox"
            value={otp}
            onChange={(e) => {
              setOtp(e.target.value);
              setError("");
            }}
            onKeyDown={handleKeyDown}
          />
          {error && <p className="errorText">{error}</p>}
          {message && <p className="successText">{message}</p>}

          <div className="resend-container">
            {resending ? (
              <span className="resend-timer">Sending…</span>
            ) : canResend ? (
              <span className="resend-active" onClick={handleResendOTP}>
                Resend OTP
              </span>
            ) : (
              <span className="resend-timer">
                Resend OTP in <span>{timer}s</span>
              </span>
            )}
          </div>

          <Button
            className="btn"
            onClick={handleVerifyOTP}
            disabled={loading}
            style={{ backgroundColor: "var(--brand, #B87333)", borderColor: "var(--brand, #B87333)" }}
          >
            Verify OTP
          </Button>
        </div>
        <div className="paraText clickable" onClick={() => navigate("/Login")} style={{ cursor: "pointer" }}>
          <span><MdKeyboardArrowLeft className="arrowIcon" /></span> Go Back
        </div>
      </Styled.MainContainer>
    </Styled.LoginContainer>
  );
};

export default ForgotPassword;
